import defaultStyles from "./help-default.module.scss";
import mobileStyles from "./help-mobile.module.scss";
import { useTranslation } from "react-i18next";
import { useServices } from "@/services-context.tsx";
import { STATIC_PAGE_LISTENER_NAME } from "@/context/static-pages.ts";
import { LayoutStyleTypes, LayoutTypes, useLayoutType } from "@/util-react/layout.tsx";
import { Url } from "@/helpers/url.ts";
import DomPurify from "isomorphic-dompurify";
import { useMemo } from "preact/hooks";
import classNames from "classnames";
import { useRoute } from "preact-iso";

const prepareMenuItems = (dom: Document) => {
  const elements = dom.getElementsByClassName("static-page-header");

  return Array.from(elements)
    .filter((element): element is HTMLElement => element instanceof HTMLElement)
    .map((element) => ({
      hash: trimSpecialChars(element.dataset.hash ?? ""),
      label: element.textContent,
      url: prepareMenuUrl(element.dataset.hash)
    }));
};

const parseDocument = (staticPageContent: string) => {
  const menu = [] as { hash: string; label: string; url: string }[];
  const sections = [] as HTMLElement[];

  const parser = new DOMParser();
  const dom = parser.parseFromString(staticPageContent, "text/html");

  for (const element of dom.getElementsByClassName("static-page-option")) {
    const labelElement = element.querySelector(".static-page-header")!;
    const labelText = labelElement.textContent!;

    menu.push({
      hash: trimSpecialChars(labelText),
      label: labelText,
      url: prepareMenuUrl(labelText),
    });

    sections.push(element as HTMLElement);
  };

  return {
    menu,
    sections
  };
};

const trimSpecialChars = (str: string) => {
  return str.trim().toLowerCase().replaceAll(" ", "-");
};

const prepareMenuUrl = (label: string | undefined | null) => {
  if (!label) {
    return "#";
  }

  return Url.to("help-page", { section: trimSpecialChars(label) });
};

export function useHelpCommon(staticPageContent: string) {
  const { t } = useTranslation();
  const services = useServices([STATIC_PAGE_LISTENER_NAME]);
  const { layoutType } = useLayoutType();
  const isMobile = layoutType === LayoutTypes.TYPE_MOBILE;
  const route = useRoute();
  const section = route.query.section;

  const additionalStyles = isMobile ? mobileStyles : defaultStyles;

  const { menu, sections } = useMemo(() => {
    return parseDocument(staticPageContent);
  }, [staticPageContent]);

  const index = useMemo(() => {
    const index = menu.findIndex(item => item.hash === section);

    return index >= 0 ? index : 0;
  }, [menu, section]);

  const content = useMemo(() => {
    const section = sections[index]!;
    const content = DomPurify.sanitize(section.outerHTML, { RETURN_DOM: false });

    return content;
  }, [index, sections]);

  const renderSectionMenu = () => {
    return (
      <div className="menu">
        {menu.map((item, key) => {
          const isActive = item.hash === section;
          const menuSectionClass = classNames({
            "menu-section": true,
            active: isActive,
          });

          return (
            <div key={key} className={menuSectionClass}>
              <a href={item.url}>
                <div className="menu-section-label">{item.label}</div>
              </a>
            </div>
          );
        })}
      </div>
    );
  };

  const renderContent = () => {
    const sectionClassName = classNames({
      "content": true,
      [defaultStyles[LayoutStyleTypes.TYPE_SM]]: isMobile,
    });

    return (
      <div className={defaultStyles.wrapper}>
        {
          !isMobile && (
            <div className={additionalStyles.topcard}>
              <div className={additionalStyles.rowTitle}>
                <h1 className={additionalStyles.title}>{t("1h06pmbvy0noasg3.help", "Help")}</h1>
              </div>
            </div>
          )
        }
        <div className={sectionClassName}>
          {renderSectionMenu()}
          <div dangerouslySetInnerHTML={{ __html: content! }} />
        </div>
      </div>
    );
  };

  return {
    renderContent
  };
}
